import {
  HomePageCountryOverride,
  HomePageVisibilitySettings
} from 'src/app/admin/tf-admin/tf-configuration/tf-environments/tf-environments.model';
import { SkipPublicPageApiRecord, SkipPublicPageSavePayload } from './skip-public-page.model';

export function normalizeCountryCode(code: string | null | undefined): string {
  return (code ?? '').trim().toUpperCase();
}

export function isGeneralSkipPublicPageRecord(record: SkipPublicPageApiRecord): boolean {
  if (record.default === true) {
    return true;
  }

  const country = normalizeCountryCode(record.country);
  return !country && record.assign_to_country !== true;
}

export function isCountrySkipPublicPageRecord(record: SkipPublicPageApiRecord): boolean {
  if (record.assign_to_country === true) {
    return true;
  }

  return !!normalizeCountryCode(record.country);
}

export function apiRecordsToUiSettings(
  records: SkipPublicPageApiRecord[] | null | undefined,
  resolveCountryName: (code: string) => string
): HomePageVisibilitySettings {
  const list = Array.isArray(records) ? records : [];
  const general = list.find(isGeneralSkipPublicPageRecord);
  const enabled = general?.display_home ?? false;

  const countryOverrides = list
    .filter(isCountrySkipPublicPageRecord)
    .map((record) => {
      const code = normalizeCountryCode(record.country);
      return {
        id: record.id,
        code,
        name: resolveCountryName(code),
        visible: record.display_home ?? false
      } satisfies HomePageCountryOverride;
    })
    .filter((country) => country.code);

  return { enabled, countryOverrides };
}

export function uiSettingsToSavePayload(
  settings: HomePageVisibilitySettings
): SkipPublicPageSavePayload {
  return {
    display_home: settings.enabled,
    countries: settings.countryOverrides
      .map((country) => ({
        country: normalizeCountryCode(country.code),
        display_home: country.visible
      }))
      .filter((country) => country.country)
  };
}

/**
 * Visitor access: country override wins when present, otherwise general `display_home`.
 */
export function resolveHomePageAccess(
  globalDisplayHome: boolean,
  countryCode: string | null | undefined,
  overrides: HomePageCountryOverride[]
): boolean {
  const normalized = normalizeCountryCode(countryCode);
  if (normalized) {
    const rule = overrides.find((override) => override.code === normalized);
    if (rule) {
      return rule.visible;
    }
  }

  return globalDisplayHome;
}

/** True when per-country visibility matches the general toggle (override has no effect). */
export function isCountryOverrideRedundant(
  globalDisplayHome: boolean,
  country: HomePageCountryOverride
): boolean {
  return globalDisplayHome === country.visible;
}
